import { RawDraftContentState, RawDraftEntity } from 'draft-js';
import { isString } from 'lodash';
import { PossibleActions } from '../../../../components/Draft/DraftActions';
import {
  TABLE_CONTENT_STATE_DATA_TYPE,
  TABLE_PLUGIN_DATA_TYPE,
} from '../../../../components/Draft/plugins/table/constants';
import {
  isInternalUrl,
  patternPeopleLinkRegex,
  patternRecipeLinkRegex,
  patternSlideShowRegex,
} from './constants';
import { GetLinksType, LabelLinkType, LinkSource, LinkType } from './types';

const FRAME_BLOCK_TYPE = 'aside';
const QUOTE_BLOCK_TYPE = 'quote';

const LINK_ENTITY_TYPES: ReadonlySet<LinkType> = new Set([
  PossibleActions.LINK,
  PossibleActions.INTERNAL_LINK,
  PossibleActions.PERSON_LINK,
]);

const parseRawDraftContent = (value: unknown): RawDraftContentState | null => {
  if (!value) {
    return null;
  }

  if (isString(value)) {
    try {
      return JSON.parse(value) as RawDraftContentState;
    } catch {
      return null;
    }
  }

  const withToJS = value as { toJS?: () => RawDraftContentState };
  if (typeof withToJS.toJS === 'function') {
    return withToJS.toJS();
  }

  const asDraft = value as RawDraftContentState;
  if (asDraft.blocks && asDraft.entityMap) {
    return asDraft;
  }

  return null;
};

const getLabelLinkType = (linkType: LinkType, link?: string): LabelLinkType => {
  if (linkType === PossibleActions.PERSON_LINK) {
    return 'People';
  }

  if (
    link &&
    [PossibleActions.LINK, PossibleActions.INTERNAL_LINK].includes(linkType)
  ) {
    if (link.match(new RegExp(patternRecipeLinkRegex))) {
      return 'Recipe';
    }
    if (link.match(new RegExp(patternSlideShowRegex))) {
      return 'Diapo';
    }
    if (link.match(new RegExp(patternPeopleLinkRegex))) {
      return 'People';
    }
  }

  return 'Lien';
};

const getEntityLabel = (
  content: RawDraftContentState,
  entityKey: string,
  entity: RawDraftEntity,
): string => {
  const children = entity.data?.children;
  if (children) {
    return `${children}`;
  }

  const entityKeyNum = Number(entityKey);
  const blockWithEntity = content.blocks.find((block) =>
    block.entityRanges?.some((range) => range.key === entityKeyNum),
  );

  if (!blockWithEntity) {
    return '';
  }

  const entityRange = blockWithEntity.entityRanges.find(
    (range) => range.key === entityKeyNum,
  );

  if (!entityRange) {
    return '';
  }

  return blockWithEntity.text.substring(
    entityRange.offset,
    entityRange.offset + entityRange.length,
  );
};

const extractLinksFromContentState = (
  content: RawDraftContentState,
  source?: LinkSource,
): GetLinksType[] =>
  Object.entries(content.entityMap ?? {}).reduce<GetLinksType[]>(
    (accum, [entityKey, entity]) => {
      const linkType = entity.type as LinkType;
      if (!LINK_ENTITY_TYPES.has(linkType)) {
        return accum;
      }

      const url = entity.data?.url;
      if (!url) {
        return accum;
      }

      const isInternal =
        linkType === PossibleActions.INTERNAL_LINK || isInternalUrl(url);

      return [
        ...accum,
        {
          label: getEntityLabel(content, entityKey, entity),
          link: `${url}`,
          type: getLabelLinkType(linkType, url),
          isInternal,
          source,
          entityKey,
        },
      ];
    },
    [],
  );

const nestedStatesFromAtomicBlock = (
  block: RawDraftContentState['blocks'][number],
): { content: RawDraftContentState; source?: LinkSource }[] => {
  if (block.type !== 'atomic' || !block.data) {
    return [];
  }

  const { data } = block;

  const frameDraft =
    data.type === FRAME_BLOCK_TYPE
      ? parseRawDraftContent(data.frameDraft)
      : null;

  const quoteDraft =
    data.type === QUOTE_BLOCK_TYPE
      ? parseRawDraftContent(data.quoteDraft)
      : null;

  const tableCells =
    data.type === TABLE_PLUGIN_DATA_TYPE
      ? parseRawDraftContent(data.tableContentState)?.blocks.flatMap(
          (cellBlock) => {
            if (cellBlock.type !== TABLE_CONTENT_STATE_DATA_TYPE) {
              return [];
            }
            const cellContent = parseRawDraftContent(
              cellBlock.data?.cellContentState,
            );
            return cellContent
              ? [{ content: cellContent, source: 'Tableau' as const }]
              : [];
          },
        ) ?? []
      : [];

  const slideshowDescription = parseRawDraftContent(
    data.slideshow?.description,
  );

  return [
    ...(frameDraft
      ? [{ content: frameDraft, source: 'Encadré' as const }]
      : []),
    ...(quoteDraft
      ? [{ content: quoteDraft, source: 'Citation' as const }]
      : []),
    ...tableCells,
    ...(slideshowDescription ? [{ content: slideshowDescription }] : []),
  ];
};

const atomicUrlEntries = (
  data: Record<string, unknown>,
): { url: string; label: string; source?: LinkSource }[] => {
  const slideshow = data.slideshow as
    | { buttonUrl?: string; buttonTitle?: string }
    | undefined;

  return [
    ...(typeof data.url === 'string' && data.url
      ? [
          {
            url: data.url,
            label: `${data.buttonLabel || data.url}`,
            source: 'Bouton' as const,
          },
        ]
      : []),
    ...(typeof slideshow?.buttonUrl === 'string' && slideshow.buttonUrl
      ? [
          {
            url: slideshow.buttonUrl,
            label: `${slideshow.buttonTitle || slideshow.buttonUrl}`,
          },
        ]
      : []),
  ];
};

const extractAtomicBlockLinks = (body: RawDraftContentState): GetLinksType[] =>
  body.blocks.flatMap((block) => {
    if (block.type !== 'atomic' || !block.data) {
      return [];
    }

    const { data } = block;

    return atomicUrlEntries(data).map(({ url, label, source }) => {
      const isInternal =
        data.rendering === 'lien-interne' || isInternalUrl(url);

      return {
        label,
        link: url,
        type: getLabelLinkType(
          isInternal ? PossibleActions.INTERNAL_LINK : PossibleActions.LINK,
          url,
        ),
        isInternal,
        source,
      };
    });
  });

const markDuplicates = (links: GetLinksType[]): GetLinksType[] => {
  const urlCounts = links.reduce<Record<string, number>>(
    (counts, { link }) => ({
      ...counts,
      [link]: (counts[link] ?? 0) + 1,
    }),
    {},
  );

  return links.map((link) =>
    (urlCounts[link.link] ?? 0) > 1
      ? { ...link, type: 'Doublon' as LabelLinkType }
      : link,
  );
};

export const extractAllLinksFromBody = (
  body: RawDraftContentState | null,
): GetLinksType[] => {
  if (!body) {
    return [];
  }

  const draftLinks = [
    ...extractLinksFromContentState(body),
    ...body.blocks
      .flatMap(nestedStatesFromAtomicBlock)
      .flatMap(({ content, source }) =>
        extractLinksFromContentState(content, source),
      ),
  ];

  return markDuplicates([...draftLinks, ...extractAtomicBlockLinks(body)]);
};

export const hasDuplicateLinks = (body: RawDraftContentState | null): boolean =>
  extractAllLinksFromBody(body).some(({ type }) => type === 'Doublon');
